import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Language } from '../types';
import { ConfirmDialog, type ConfirmDialogProps } from '../components/common/ConfirmDialog';

export type ConfirmOptions = {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  hideConfirm?: boolean;
};

type ConfirmContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

type PendingConfirm = ConfirmOptions & { id: number };

export function ConfirmProvider({ lang, children }: { lang: Language; children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setPending({ ...options, id: Date.now() });
    });
  }, []);

  const finish = (result: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    // Resolve after clearing, so a second confirm opened from this one is not wiped.
    resolve?.(result);
  };

  const dialogProps: ConfirmDialogProps | null = pending
    ? {
        open: true,
        lang,
        title: pending.title,
        message: pending.message,
        confirmLabel: pending.confirmLabel,
        cancelLabel: pending.cancelLabel,
        destructive: pending.destructive,
        hideConfirm: pending.hideConfirm,
        onConfirm: () => finish(true),
        onCancel: () => finish(false),
      }
    : null;

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {dialogProps && <ConfirmDialog {...dialogProps} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return ctx.confirm;
}
